<script setup>
// The kit's scenario card speaking this page's language (phase 4.4). A scenario's instruction is stored as markdown
// with the tools as links (`[Add private note](tool://add_private_note)`); the kit's card shows plain text, so a
// tool reads `@Add private note` there, and the tools it uses are its badges, by name. Events go back to the page
// in the shape it already knows: { action, id } and select(id).
// Selection: the kit's card has no checkbox, the woot's list selects scenarios for a bulk delete; the checkbox sits
// beside the card (the RuleCard's own place for it), not inside the kit's card.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ScenarioCard from 'dashboard/components-next/polaris/cards/ScenarioCard.vue';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';

const props = defineProps({
  scenario: { type: Object, required: true },
  selectable: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['action', 'select']);

const { t } = useI18n();
const tools = useMapGetter('polarisTools/getRecords');

const toolName = id => tools.value?.find(tool => tool.id === id)?.title || id;

const instruction = computed(() =>
  (props.scenario.instruction || '')
    .replace(/\[([^\]]+)\]\(tool:\/\/[^)]+\)/g, '@$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
);
const toolNames = computed(() => (props.scenario.tools || []).map(toolName));
</script>

<template>
  <div class="flex min-w-0 items-start gap-2" data-scenario-skin>
    <label
      v-if="selectable"
      class="mt-4 flex size-6 shrink-0 items-center justify-center"
    >
      <Checkbox
        :model-value="selected"
        :aria-label="t('POLARIS.CARDS.SELECT', { item: scenario.title })"
        @update:model-value="emit('select', scenario.id)"
      />
    </label>
    <!-- A tool with a long name would push its badge out of the card (the kit's Badge does not wrap): here it is cut
         with an ellipsis. Asked of the kit in the phase record. -->
    <ScenarioCard
      class="min-w-0 flex-1 [&_[data-slot=badge]]:max-w-full [&_[data-slot=badge]]:truncate"
      :title="scenario.title"
      :description="scenario.description"
      :instruction="instruction"
      :tools="toolNames"
      :inactive="!scenario.enabled"
      @action="({ action }) => emit('action', { action, id: scenario.id })"
    />
  </div>
</template>
